// src/pages/Administration/Members/MemberForm.tsx
/**
 * The editable half of a member record: the fields in `EDITABLE_FIELDS` and nothing else.
 *
 * Shared by the edit drawer and the add form so the two can never drift into offering different fields.
 * The protected fields are **not** rendered here at all — the edit drawer shows them separately, as
 * read-only facts, which keeps "this is a form field" and "this is a fact about the record" visually
 * distinct instead of relying on a `disabled` attribute to carry that meaning.
 */
import { Grid, Select, Stack, TextInput } from '@/components/ui';
import { DatePickerInput } from '@/components/ui';
import type { MaritalStatus } from '@actschurch/ccms-sdk';
import { toWireDate, type MemberFormValues } from './memberFields';

/** The values stored on `Member.gender`, matching the profile screen. */
const GENDER_OPTIONS = [
  { value: 'Male', label: 'Male' },
  { value: 'Female', label: 'Female' },
];

export interface MemberFormProps {
  values: MemberFormValues;
  errors: Partial<Record<keyof MemberFormValues, string>>;
  maritalStatuses: MaritalStatus[];
  disabled?: boolean;
  onChange: (patch: Partial<MemberFormValues>) => void;
}

export default function MemberForm({
  values, errors, maritalStatuses, disabled = false, onChange,
}: MemberFormProps) {
  // ⚠️ Mantine hands back a `Date`; the record stores a string. Converted at the edge so nothing else
  // in this feature has to know that both shapes exist.
  const dobValue = values.dateOfBirth ? new Date(values.dateOfBirth) : null;

  return (
    <Stack gap="md">
      <Grid gutter="md">
        <Grid.Col span={{ base: 12, sm: 4 }}>
          <TextInput
            label="First name"
            required
            value={values.firstName}
            error={errors.firstName}
            disabled={disabled}
            onChange={(e) => onChange({ firstName: e.currentTarget.value })}
          />
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 4 }}>
          <TextInput
            label="Middle name"
            value={values.middleName}
            disabled={disabled}
            onChange={(e) => onChange({ middleName: e.currentTarget.value })}
          />
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 4 }}>
          <TextInput
            label="Surname"
            required
            value={values.lastName}
            error={errors.lastName}
            disabled={disabled}
            onChange={(e) => onChange({ lastName: e.currentTarget.value })}
          />
        </Grid.Col>

        <Grid.Col span={{ base: 12, sm: 4 }}>
          <DatePickerInput
            label="Date of birth"
            placeholder="Not on record"
            clearable
            maxDate={new Date()}
            value={dobValue}
            error={errors.dateOfBirth}
            disabled={disabled}
            onChange={(d) => onChange({
              dateOfBirth: d ? (toWireDate((d as Date).toISOString()) ?? '') : '',
            })}
          />
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 4 }}>
          <Select
            label="Gender"
            placeholder="Not on record"
            data={GENDER_OPTIONS}
            clearable
            value={values.gender || null}
            disabled={disabled}
            onChange={(v) => onChange({ gender: v ?? '' })}
          />
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 4 }}>
          <Select
            label="Marital status"
            placeholder="Not on record"
            data={maritalStatuses.map((s) => ({
              value: String(s.maritalStatusID),
              label: s.name,
            }))}
            clearable
            searchable
            value={values.maritalStatusID != null ? String(values.maritalStatusID) : null}
            disabled={disabled}
            onChange={(v) => onChange({ maritalStatusID: v ? Number(v) : null })}
          />
        </Grid.Col>

        <Grid.Col span={{ base: 12, sm: 6 }}>
          <TextInput
            label="Email"
            required
            description="Used to match this person against sign-ups and requests."
            value={values.email}
            error={errors.email}
            disabled={disabled}
            onChange={(e) => onChange({ email: e.currentTarget.value })}
          />
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 6 }}>
          <TextInput
            label="Cellphone"
            value={values.cellNumber}
            disabled={disabled}
            onChange={(e) => onChange({ cellNumber: e.currentTarget.value })}
          />
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 6 }}>
          <TextInput
            label="Alternative contact"
            value={values.altContact}
            disabled={disabled}
            onChange={(e) => onChange({ altContact: e.currentTarget.value })}
          />
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 6 }}>
          <TextInput
            label="Telephone"
            value={values.telephone}
            disabled={disabled}
            onChange={(e) => onChange({ telephone: e.currentTarget.value })}
          />
        </Grid.Col>
      </Grid>
    </Stack>
  );
}
